<template>
  <div class="kyc-flow-agreements">
    <header class="kyc-flow-agreements__header">
      <BaseText
        as="h1"
        variant="headline-2-semibold"
        class="kyc-flow-agreements__header-title"
      >
        By submitting this application you agree to the following agreements:
      </BaseText>
    </header>

    <div class="kyc-flow-agreements__content">
      <div
        ref="agreementsContainer"
        class="kyc-flow-agreements__agreements-container"
      >
        <ul class="kyc-flow-agreements__sections">
          <li
            v-for="(section, index) in sections"
            :key="index"
            :class="[
              'kyc-flow-agreements__sections-item',
              'kyc-flow-agreements__item',
              `kyc-flow-agreements__item--${index}`,
              { 'kyc-flow-agreements__item--active': section.active },
            ]"
          >
            <div class="kyc-flow-agreements__item-container">
              <header
                class="kyc-flow-agreements__item-header"
                @click="toggleSection(index)"
              >
                <div class="kyc-flow-agreements__item-header-title-container">
                  <BaseIcon
                    name="tick-circle-filled"
                    class="kyc-flow-agreements__item-header-icon"
                  />
                  <BaseText
                    as="h2"
                    variant="headline-6-bold"
                    class="kyc-flow-agreements__item-header-title"
                  >
                    {{ section.name }}
                  </BaseText>
                </div>
                <BaseIcon
                  name="chevron-right"
                  class="kyc-flow-agreements__item-header-icon kyc-flow-agreements__item-header-icon--chevron"
                />
              </header>
              <div class="kyc-flow-agreements__item-content">
                <component :is="section.component" />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import TermsOfService from "./Agreements/TermsOfService.vue";
import PrivacyPolicy from "./Agreements/PrivacyPolicy.vue";
import ESign from "./Agreements/E-Sign.vue";
import ChargeCardAgreement from "./Agreements/ChargeCardAgreement.vue";
import RatesFees from "./Agreements/RatesFees.vue";
import PrepaidAgreement from "./Agreements/PrepaidAgreement.vue";
import PrepaidLongForm from "./Agreements/PrepaidLongForm.vue";
import PrepaidShortForm from "./Agreements/PrepaidShortForm.vue";
import BaseText from "@/library/BaseText.vue";
import BaseIcon from "@/library/BaseIcon.vue";

interface Section {
  name: string;
  component: any;
  active: boolean;
  offsetTop?: number;
}

let FIXED_HEADER_HEIGHT = 0;
const agreementsContainer = ref<HTMLElement | null>(null);
const sections = ref<Section[]>([
  { name: "Rates & Fees", component: RatesFees, active: true },
  { name: "Terms of Service", component: TermsOfService, active: false },
  { name: "Privacy Policy", component: PrivacyPolicy, active: false },
  {
    name: "Charge Card Agreement",
    component: ChargeCardAgreement,
    active: false,
  },
  { name: "Prepaid Agreement", component: PrepaidAgreement, active: false },
  { name: "Prepaid - Long form", component: PrepaidLongForm, active: false },
  { name: "Prepaid - Short form", component: PrepaidShortForm, active: false },
  { name: "E-Sign", component: ESign, active: false },
]);

onMounted(() => {
  calcOffsets();
});

function toggleSection(index: number) {
  sections.value.forEach((sec, i) => {
    sec.active = i === index ? !sec.active : false;
  });
}

function calcOffsets() {
  const container = agreementsContainer.value;
  if (!container) return;
  // measure fixed page header
  const pageHeader = document.querySelector<HTMLElement>(
    ".kyc-flow-agreements__header"
  );
  FIXED_HEADER_HEIGHT = pageHeader ? pageHeader.offsetHeight : 0;
  console.log("FIXED_HEADER_HEIGHT", FIXED_HEADER_HEIGHT);

  // measure each item's header offsetTop relative to container
  const headers = container.querySelectorAll<HTMLElement>(
    ".kyc-flow-agreements__item-header"
  );
  headers.forEach((header, idx) => {
    sections.value[idx].offsetTop = header.offsetTop;
  });
}

watch(
  () => sections.value.map((s) => s.active),
  () => {
    // find active index
    const idx = sections.value.findIndex((s) => s.active);
    if (idx === -1) return;
    const container = agreementsContainer.value;
    const offset = sections.value[idx].offsetTop ?? 0;
    container?.scrollTo({
      top: offset - FIXED_HEADER_HEIGHT - 400,
      behavior: "smooth",
    });
  }
);
</script>

<style lang="scss" scoped>
/* stylelint-disable */
@mixin scroll-gradient($direction: bottom) {
  display: block;
  height: 30px;
  position: absolute;
  z-index: 1;
  width: 100%;
  background: linear-gradient(
    to $direction,
    rgba($color-base-white-100-light, 0),
    rgba($color-base-white-100-light, 1)
  );

  @at-root .theme-dark & {
    background: linear-gradient(
      to $direction,
      rgba($color-base-white-100-dark, 0),
      rgba($color-base-white-100-dark, 1),
      rgba($color-base-white-100-dark, 1)
    );
  }
}

$component-name: "kyc-flow-agreements";

.#{$component-name} {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-bottom: 24px;

  &__header {
    margin-top: 24px;

    &-title {
      color: $color-primary-100;
      font-size: 29px;
      font-weight: 600;
      text-align: center;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 0;
    margin-top: 24px;
    position: relative;
  }

  &__agreements-container {
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding-block: 30px;
    overflow-y: auto;

    &::after {
      content: "";
      bottom: 0;
      right: 16px;
      @include scroll-gradient;
    }

    &::before {
      content: "";
      top: 0;
      right: 16px;
      @include scroll-gradient(top);
    }
  }

  &__sections {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-right: 16px;
  }

  &__item {
    &-container {
      background: $color-primary-5;
      border: 1px solid $color-base-black-15;
      border-radius: 30px;
      transition:
        border 0.2s ease-in-out,
        max-height 0.2s ease-in-out,
        background 0.2s ease-in-out;

      .#{$component-name}__item--active & {
        border: 1px solid $color-primary-100;
        overflow: auto;
        max-height: 100%;
        box-shadow: 0 5px 20px 0 rgba(0, 0, 0, 0.05);
        background: rgba($color-base-white-80-light, 0.8);
        backdrop-filter: blur(13px);

        @at-root .theme-dark & {
          background: rgba($color-base-white-80-dark, 0.8);
        }
      }
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      padding: 16px;
      transition: padding 0.4s cubic-bezier(0.4, 0, 0.2, 1);

      .#{$component-name}__item--active & {
        padding-bottom: 0;
      }

      &-title-container {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      &-icon {
        color: $color-primary-100;
        font-size: 24px;

        &--chevron {
          font-size: 18px;
          transition: transform 0.2s ease-in-out;

          .#{$component-name}__item--active & {
            transform: rotate(90deg);
          }
        }
      }
    }

    &-content {
      max-height: 0;
      overflow: hidden;
      transition:
        max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
        padding 0.4s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.3s ease-in-out;
      padding-inline: 16px;
      padding-top: 0;
      padding-bottom: 0;
      opacity: 0;

      .#{$component-name}__item--active & {
        max-height: 19000px;
        padding-block: 16px;
        opacity: 1;
      }
    }
  }
}
</style>
